import { cosmosChannels } from '@shared/data/bridge'
import { HttpRequestException } from '@injectivelabs/exceptions'
import { ChainRestBankApi } from '@injectivelabs/sdk-ts/client/chain'
import { createCosmosStrategyFactory } from '@injectivelabs/wallet-cosmos-strategy'
import { getEndpointsFromChainId } from '@/app/data/endpoints'
import type { Network } from '@shared/types'
import type { Wallet } from '@injectivelabs/wallet-base'
import type { CosmosChainId } from '@injectivelabs/ts-types'

export const fetchCosmosAddress = async ({
  wallet,
  network
}: {
  wallet: Wallet
  network: Network
}) => {
  const channel = cosmosChannels[network]

  if (!channel) {
    return
  }

  const cosmosWalletStrategy = createCosmosStrategyFactory({
    wallet,
    chainId: channel.aChainId as CosmosChainId
  })

  try {
    const addresses = await cosmosWalletStrategy.getAddresses()
    const [address] = addresses

    return address
  } catch {
    // silently fail
  }
}

export const fetchCosmosBalance = async ({
  address,
  network
}: {
  address: string
  network: Network
}) => {
  const channel = cosmosChannels[network]

  if (!channel) {
    return
  }

  try {
    const endpoints = getEndpointsFromChainId(channel.aChainId as CosmosChainId)

    if (!endpoints) {
      throw new HttpRequestException(
        new Error(`Endpoints for ${channel.aChainId} not found`)
      )
    }

    const chainRestBankApi = new ChainRestBankApi(endpoints.rest)

    const { balances } = await chainRestBankApi.fetchBalances(address)

    return balances
  } catch {
    // silently fail
  }
}

export const fetchCosmosAddressAndBalance = async ({
  wallet,
  network
}: {
  wallet: Wallet
  network: Network
}) => {
  const channel = cosmosChannels[network]

  if (!channel) {
    return
  }

  const cosmosWalletStrategy = createCosmosStrategyFactory({
    wallet,
    chainId: channel.aChainId as CosmosChainId
  })

  try {
    const addresses = await cosmosWalletStrategy.getAddresses()
    const [address] = addresses

    if (!address) {
      return undefined
    }

    const endpoints = getEndpointsFromChainId(channel.aChainId as CosmosChainId)

    if (!endpoints) {
      throw new HttpRequestException(
        new Error(`Endpoints for ${channel.aChainId} not found`)
      )
    }

    const chainRestBankApi = new ChainRestBankApi(endpoints.rest)

    const { balances } = await chainRestBankApi.fetchBalances(address)

    return {
      address,
      balances
    }
  } catch {
    // silently fail
  }
}
